/**
 * Permission gate component for conditional rendering based on permissions
 */

import { forwardRef, ReactElement, cloneElement } from 'react';
import { usePermissions } from '../hooks/usePermissions';
import type { PermissionGateProps } from '../types';

/**
 * Permission gate component for rendering content based on user permissions
 *
 * @example
 * ```tsx
 * <PermissionGate roles={['admin', 'staff']} fallback={<div>Access Denied</div>}>
 *   <AdminPanel />
 * </PermissionGate>
 *
 * <PermissionGate permissions={['edit:users']} mode="disable">
 *   <Button>Edit User</Button>
 * </PermissionGate>
 * ```
 */
export const PermissionGate = forwardRef<HTMLDivElement, PermissionGateProps>(
  (props, ref) => {
    const {
      children,
      fallback = null,
      roles,
      permissions,
      requireAll = false,
      mode = 'hide',
    } = props;

    const { canAccess } = usePermissions();

    const hasAccess = canAccess(roles, permissions, requireAll);

    // Mode: hide - don't render anything if no access
    if (mode === 'hide' && !hasAccess) {
      return fallback ? <div ref={ref}>{fallback}</div> : null;
    }

    // Mode: disable - render but disable if no access
    if (mode === 'disable' && !hasAccess) {
      if (!children || typeof children === 'string') {
        return <div ref={ref}>{children}</div>;
      }

      // Clone element and add disabled prop
      const child = children as ReactElement;
      return (
        <div ref={ref}>
          {cloneElement(child, {
            ...child.props,
            disabled: true,
            style: {
              ...child.props.style,
              opacity: 0.5,
              cursor: 'not-allowed',
            },
          })}
        </div>
      );
    }

    // Mode: blur - render but blur if no access
    if (mode === 'blur' && !hasAccess) {
      return (
        <div
          ref={ref}
          style={{
            filter: 'blur(4px)',
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        >
          {children}
        </div>
      );
    }

    // Has access - render normally
    return <div ref={ref}>{children}</div>;
  }
);

PermissionGate.displayName = 'PermissionGate';

export default PermissionGate;
